import React from "react";
import Button from "app/popleads/components/ui-kit/Button";
import { Body, BodyStrong, Caption, FieldLabel } from "app/popleads/components/ui-kit/Text";

import styles from "./AssistantMergeApproval.module.scss";

/**
 * The one thing the assistant asks permission for.
 *
 * Merging deletes a profile and cannot be undone, so the provider stops the turn and hands back
 * which two records are involved rather than doing it. Everything on this card exists so the
 * decision can be made without leaving the conversation: who each person is, and how much history
 * sits on each — a record with twelve won deals behind it is not the one to remove, and that is
 * not visible from two ids.
 *
 * The removed side is styled as the loss it is — a colored rule and label, not a fill, so the
 * card reads as a decision rather than a status board. The two sides are deliberately not
 * interchangeable-looking: the mistake this card exists to prevent is agreeing to the wrong
 * direction.
 *
 * A decided card stays in the thread. It is not a dialog that has served its purpose — it is what
 * the assistant said and what the user answered, and a conversation that silently loses the record
 * of an irreversible decision cannot be scrolled back through to see what was agreed to. Only the
 * buttons go; the two sides stay, relabelled to say what happened.
 */
const LABELS = {
    /* Imperative while it is still a proposal — pending, or a proposal that was turned down, since
       a refusal did not produce an outcome to describe. Past tense only once the records have
       actually moved. */
    approved: { remove: "Removed", keep: "Kept" },
    default: { remove: "Remove", keep: "Keep" },
};

const HEADINGS = {
    approved: "Profiles merged",
    declined: "Merge cancelled",
    default: "Confirm this merge",
};

const LedgerSide = ({ label, tone, profile }) => (
    <div className={`${styles.side} ${styles[tone]}`}>
        <FieldLabel className={styles.eyebrow}>
            <span className={styles.dot} aria-hidden="true" />
            {label}
        </FieldLabel>
        <BodyStrong className={styles.name}>{profile.name || `Profile ${profile.id}`}</BodyStrong>
        {profile.company && <Body className={styles.line}>{profile.company}</Body>}
        {profile.email && <Body className={styles.line}>{profile.email}</Body>}
        <Caption className={styles.meta}>
            {profile.wonDeals ? `${profile.wonDeals} won deals` : "no won deals"} · id {profile.id}
        </Caption>
    </div>
);

/**
 * What the merge destroys, which the two sides above cannot show.
 *
 * A field both records fill appears on each side as that side's value, and nothing in that layout
 * says which of the two stops existing. These are the ones that do: the kept profile already has
 * its own, so the removed profile's value is not copied across and no snapshot is kept of it.
 *
 * It stays on the card after the decision for the same reason the two sides do — afterwards this
 * list is the only record of what the merge actually cost.
 */
const DiscardedValues = ({ values, decision }) => {
    if (!values || values.length === 0) return null;

    return (
        <div className={styles.discarded}>
            <FieldLabel className={styles.discardedLabel}>
                {decision === "approved" ? "Values not kept" : "Will not be kept"}
            </FieldLabel>
            {values.map(({ field, value }) => (
                <Body key={field} className={styles.discardedLine}>
                    <span className={styles.discardedField}>{field}</span> {value}
                </Body>
            ))}
            <Caption className={styles.discardedNote}>
                {decision === "approved"
                    ? "The kept profile had its own, so these are no longer anywhere in the CRM."
                    : "The kept profile has its own, so these will not exist anywhere afterwards."}
            </Caption>
        </div>
    );
};

const AssistantMergeApproval = ({ approval, result, isBusy, onDecide }) => {
    const decision = approval.decision || null;
    const labels = LABELS[decision] || LABELS.default;

    const remove = {
        id: approval.removeProfileId,
        name: approval.removeProfileName,
        company: approval.removeProfileCompany,
        email: approval.removeProfileEmail,
        wonDeals: approval.removeProfileWonDeals,
    };
    const keep = {
        id: approval.keepProfileId,
        name: approval.keepProfileName,
        company: approval.keepProfileCompany,
        email: approval.keepProfileEmail,
        wonDeals: approval.keepProfileWonDeals,
    };

    return (
        <section className={`${styles.card} ${decision ? styles.decided : ""} ${decision ? styles[decision] : ""}`}>
            <BodyStrong className={styles.heading}>{HEADINGS[decision] || HEADINGS.default}</BodyStrong>
            <Body className={styles.summary}>{approval.summary}</Body>

            <div className={styles.ledger}>
                <LedgerSide label={labels.remove} tone="remove" profile={remove} />
                <div className={styles.divider} aria-hidden="true" />
                <LedgerSide label={labels.keep} tone="keep" profile={keep} />
            </div>

            {/* Not on a declined card: nothing was lost, and listing losses under a merge that did
                not happen reads as though something was. */}
            {decision !== "declined" && <DiscardedValues values={approval.discardedValues} decision={decision} />}

            {/* Before the decision this is a caution; afterwards the same space says what was done,
                so the card can be read later without reconstructing it from the answer below. */}
            {!decision && (
                <Caption className={styles.warning}>
                    Deals, tasks, notes, tags and attachments move to the profile that is kept, so no history is lost —
                    but the removed profile cannot be brought back, and field values do not all move with its records.
                </Caption>
            )}
            {/* `result` is what the approved tool actually did, not just what was clicked -- absent
                on every card decided before this field existed, which is why success still reads
                as it always has (`!result`) rather than silently going quiet for old conversations.
                Only a `result` that says otherwise changes the claim this card makes. */}
            {decision === "approved" && (!result || result.status === "ok") && (
                <Caption className={styles.warning}>
                    You approved this merge. Deals, tasks, notes, tags and attachments moved to the profile that was
                    kept.
                </Caption>
            )}
            {decision === "approved" && result?.status === "error" && (
                <Caption className={styles.warning}>
                    You approved this merge, but the assistant could not confirm it completed. Check the profile in the
                    CRM before relying on this.
                </Caption>
            )}
            {decision === "declined" && (
                <Caption className={styles.warning}>You declined this merge. Nothing was changed.</Caption>
            )}

            {!decision && (
                <div className={styles.actions}>
                    <Button
                        variant="passive"
                        buttonStyle="option"
                        size="small"
                        disabled={isBusy}
                        onClick={() => onDecide(false)}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        buttonStyle="default"
                        size="small"
                        loading={isBusy}
                        disabled={isBusy}
                        onClick={() => onDecide(true)}
                    >
                        Merge them
                    </Button>
                </div>
            )}
        </section>
    );
};

export default AssistantMergeApproval;
